const onlyDigits = (phone: string) => phone.replace(/\D/g, '')

/**
 * O telefone é gravado só com dígitos, sem "+": "15875796131" tanto pode ser
 * +1 (587) 579-6131 (Canadá) quanto um celular "(15) 87579-6131". O tamanho
 * não desempata; quem desempata é a validade no Brasil, a mesma que o Phonelib
 * usa no backend para decidir para onde o WhatsApp sai: com 11 dígitos só é
 * celular BR com o 9 na 3ª posição. No NANP (+1) o 2º dígito do código de área
 * nunca é 9, então "1" + 10 dígitos não colide.
 *
 * Fica de fora, com a máscara BR de sempre, o que não pode ser DDI: "55"
 * (Brasil), "0" (nenhum DDI começa com 0) e "1" com código de área em 0/1 (não
 * existe no NANP). Na base isso é número BR malformado, assim como os de 12/14
 * dígitos, que este helper não reclassifica.
 */
export function isInternationalPhone(phone: string): boolean {
  const digits = onlyDigits(phone)

  if (digits.length !== 11) return false
  if (digits.startsWith('55') || digits.startsWith('0')) return false
  if (digits[2] === '9') return false
  if (digits[0] === '1' && (digits[1] === '0' || digits[1] === '1'))
    return false

  return true
}

/**
 * 10/11 dígitos, ou 12/13 com DDI 55, que não sejam número estrangeiro.
 */
export function isBrazilianPhone(phone: string): boolean {
  const digits = onlyDigits(phone)

  if (digits.length === 10 || digits.length === 11)
    return !isInternationalPhone(digits)

  return (
    (digits.length === 12 || digits.length === 13) && digits.startsWith('55')
  )
}

/**
 * Tira o DDI 55 do começo, exceto quando o "55" é o DDD de Santa Maria/RS:
 * 11 dígitos com o 9 na 3ª posição é celular válido de DDD 55, e é para ele
 * que o backend envia.
 */
export function toBrazilianNationalDigits(phone: string): string {
  const digits = onlyDigits(phone)

  if (!digits.startsWith('55')) return digits
  if (digits.length === 11 && digits[2] === '9') return digits

  return digits.slice(2)
}

/**
 * NANP (EUA/Canadá) ganha a máscara que o react-phone-input-2 mostra no
 * cadastro; os demais países saem como "+" + dígitos.
 */
export function formatInternationalPhone(phone: string): string {
  const digits = onlyDigits(phone)

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }

  return `+${digits}`
}

function formatPhone(phone: string) {
  if (!phone) return ''

  if (isInternationalPhone(phone)) return formatInternationalPhone(phone)

  phone = toBrazilianNationalDigits(phone)

  phone = phone.replace(/(.{0})(\d)/, '$1($2')
  phone = phone.replace(/(.{3})(\d)/, '$1)$2')
  if (phone.length == 11) {
    phone = phone.replace(/(.{2})$/, '-$1')
  } else if (phone.length == 12) {
    phone = phone.replace(/(.{3})$/, '-$1')
  } else if (phone.length == 13) {
    phone = phone.replace(/(.{4})$/, '-$1')
  } else if (phone.length == 14) {
    phone = phone.replace(/(.{5})$/, '-$1')
  } else if (phone.length > 15) {
    phone = phone.replace(/(.{6})$/, '-$1')
  }
  return phone.replace(/(-\d{4})\d+?$/, '$1')
}

export default formatPhone
